import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import apiClient from '@services/api/client'

function SettingsPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  const [settingsData, setSettingsData] = useState({
    notifications: true,
    emailNotifications: true,
    smsNotifications: false,
    privateProfile: false,
    showEmail: false,
    showPhone: true,
    language: 'fr',
    currency: 'FCFA'
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Charger les paramètres depuis le stockage local
  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = () => {
    try {
      const saved = localStorage.getItem('client_settings')
      if (saved) {
        setSettingsData(prev => ({ ...prev, ...JSON.parse(saved) }))
      }
    } catch (error) {
      // ignore
    }
  }

  const handleSaveSettings = async () => {
    setLoading(true)
    try {
      localStorage.setItem('client_settings', JSON.stringify(settingsData))
      toast.success('Paramètres enregistrés avec succès !')
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères')
      return
    }

    setLoading(true)
    try {
      await apiClient.put('/client/password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        new_password_confirmation: passwordData.confirmPassword
      })
      toast.success('Mot de passe modifié avec succès !')
      setShowPasswordModal(false)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      toast.error(error.message || 'Erreur lors du changement de mot de passe')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleDeleteAccount = async () => {
    setLoading(true)
    try {
      await apiClient.delete('/client/profile')
      toast.success('Compte supprimé avec succès')
      await logout()
      navigate('/login')
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Erreur lors de la suppression du compte')
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
    }
  }

  return (
    <div className="home-font min-h-screen bg-slate-50 py-8">
      <div className="w-[94%] md:w-[80%] mx-auto">
        <div className="mb-8">
          <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Mon compte</p>
          <h1 className="mt-1 mb-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">Paramètres</h1>
          <p className="text-gray-600">Gérez vos préférences et la sécurité de votre compte</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu latéral */}
          <div className="lg:col-span-1">
            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70 space-y-2 sticky top-20">
              <a href="#notifications" className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="font-medium text-gray-700">Notifications</span>
              </a>
              <a href="#privacy" className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="font-medium text-gray-700">Confidentialité</span>
              </a>
              <a href="#security" className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-medium text-gray-700">Sécurité</span>
              </a>
              <a href="#preferences" className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium text-gray-700">Préférences</span>
              </a>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Section Notifications */}
            <div id="notifications" className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#FFF8D6] rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E]">Notifications</h2>
                  <p className="text-sm text-gray-600">Gérez vos préférences de notifications</p>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Activer les notifications</div>
                    <div className="text-sm text-gray-600">Recevoir des notifications sur vos annonces</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.notifications}
                    onChange={(e) => setSettingsData({...settingsData, notifications: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Notifications par email</div>
                    <div className="text-sm text-gray-600">Recevoir des emails pour les événements importants</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.emailNotifications}
                    onChange={(e) => setSettingsData({...settingsData, emailNotifications: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Notifications par SMS</div>
                    <div className="text-sm text-gray-600">Recevoir des SMS pour les messages urgents</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.smsNotifications}
                    onChange={(e) => setSettingsData({...settingsData, smsNotifications: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>
              </div>
            </div>

            {/* Section Confidentialité */}
            <div id="privacy" className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E]">Confidentialité</h2>
                  <p className="text-sm text-gray-600">Contrôlez qui peut voir vos informations</p>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Profil privé</div>
                    <div className="text-sm text-gray-600">Seuls vos contacts peuvent voir votre profil</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.privateProfile}
                    onChange={(e) => setSettingsData({...settingsData, privateProfile: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Afficher mon email publiquement</div>
                    <div className="text-sm text-gray-600">Les utilisateurs peuvent voir votre adresse email</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.showEmail}
                    onChange={(e) => setSettingsData({...settingsData, showEmail: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Afficher mon téléphone</div>
                    <div className="text-sm text-gray-600">Les utilisateurs peuvent voir votre numéro de téléphone</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.showPhone}
                    onChange={(e) => setSettingsData({...settingsData, showPhone: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#F5B700] cursor-pointer"
                  />
                </label>
              </div>
            </div>

            {/* Section Sécurité */}
            <div id="security" className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E]">Sécurité</h2>
                  <p className="text-sm text-gray-600">Protégez votre compte</p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors text-left flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-800">Changer le mot de passe</div>
                    <div className="text-sm text-gray-600">Modifier votre mot de passe de connexion</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full p-4 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors text-left flex items-center justify-between border border-orange-200"
                >
                  <div>
                    <div className="font-medium text-orange-800">Se déconnecter</div>
                    <div className="text-sm text-orange-600">Fermer votre session sur cet appareil</div>
                  </div>
                  <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>

                <div className="p-4 bg-red-50 rounded-md border border-red-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-800 mb-2">Zone de danger</h3>
                      <p className="text-sm text-red-700 mb-3">Une fois votre compte supprimé, toutes vos données seront définitivement effacées. Cette action est irréversible.</p>
                      <button 
                        onClick={() => setShowDeleteModal(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                      >
                        Supprimer mon compte
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Préférences */}
            <div id="preferences" className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E]">Préférences</h2>
                  <p className="text-sm text-gray-600">Personnalisez votre expérience</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Langue</label>
                  <select
                    value={settingsData.language}
                    onChange={(e) => setSettingsData({...settingsData, language: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 bg-white"
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
                  <select
                    value={settingsData.currency}
                    onChange={(e) => setSettingsData({...settingsData, currency: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 bg-white"
                  >
                    <option value="FCFA">FCFA (Franc CFA)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="USD">USD (Dollar)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bouton Enregistrer */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="px-8 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md font-medium hover:bg-[#F5C800] transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Enregistrer les paramètres
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#002A4E]">Changer le mot de passe</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Changement...' : 'Confirmer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Suppression de compte */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-[#002A4E] mb-2">Supprimer votre compte ?</h3>
              <p className="text-gray-600">Cette action est irréversible. Toutes vos données seront définitivement supprimées.</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
              >
                {loading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SettingsPage
